import { UserDTO } from "api";
import { USER_ROLES, USER_ROLES_NAMES } from "recoils";

/**
 * Whether a single name segment should be treated as absent for display.
 * Matches `null`, `undefined`, empty string, and whitespace-only strings.
 *
 * @param part — A user's `firstName` or `lastName` (may be nullish per `UserDTO`).
 * @returns `true` if the segment contributes nothing after trimming.
 */
export const isBlankNamePart = (part: UserDTO["firstName"]): boolean =>
  part == null || String(part).trim() === "";

/**
 * Builds a display full name from `firstName` and `lastName`.
 * Omits blank segments; if both are blank, there is no name to show.
 *
 * @param user — Row user; only `firstName` and `lastName` are read.
 * @returns Trimmed `"First Last"`, a single name if only one is set, or `null` if both are blank.
 */
export const fullNameFromUser = (user: UserDTO): string | null => {
  const { firstName, lastName } = user;
  if (isBlankNamePart(firstName) && isBlankNamePart(lastName)) {
    return null;
  }
  return [firstName, lastName]
    .filter((part) => !isBlankNamePart(part))
    .map((part) => String(part).trim())
    .join(" ");
};

/**
 * Role label shown in `User`’s Autocomplete (`user.tsx`): module/super admin → Module Admin,
 * otherwise `USER_ROLES_NAMES[roles[0]]`.
 */
export const userTitleFromUser = (user: UserDTO): string => {
  const isAdmin =
    user.roles?.includes(USER_ROLES.SUPER_ADMIN) ||
    user.roles?.includes(USER_ROLES.MODULE_ADMIN);
  const label = isAdmin
    ? USER_ROLES_NAMES[USER_ROLES.MODULE_ADMIN]
    : USER_ROLES_NAMES[user.roles?.[0]];
  return (label ?? "") as string;
};

/**
 * Site names for the table column, matching `User`’s multiselect: admins see all sites
 * (`sitesOptions.filter((site) => site.value)`); others see sites from `user.sites`.
 */
export const sitesLineFromUser = (
  user: UserDTO,
  siteMap: Record<number, string>,
  allSiteIdsOrdered: number[],
): string => {
  const roles = user.roles ?? [];
  const isAdmin =
    roles.includes(USER_ROLES.SUPER_ADMIN) ||
    roles.includes(USER_ROLES.MODULE_ADMIN);
  const ids = isAdmin ? allSiteIdsOrdered : user.sites ?? [];
  if (!ids.length) {
    return "";
  }
  return ids
    .map((id) => siteMap[id])
    .filter((name): name is string => Boolean(name))
    .join(", ");
};
